import React, { memo, useCallback, useEffect, useState } from "react";
import Grid from "@ls/react-grid";
import GroupCellRenderer from "./GroupCellRenderer";
import { useGridInstance, useRowData ,useColumnDefinitionsFromCommitedState } from "state/grid";
import styled from "styled-components";
import { useGridStateModule as useGridStateModuleAzure } from "@ls/grid-state-module-azure";
import { useGridStateModule as useGridStateModuleLegacy } from "@ls/grid-state-module";
import { useSelector } from "react-redux";
import {
  selectBandedRows,
  selectGridFontSize,
  selectGridRowHeight,
  selectGridVerticalLines,
  selectHideGridSideBar
} from "state/app/selectors";
import "./CustomGrid.css";
import CustomTooltip from './CustomTooltip/CustomTooltip.js';

const getRowStyle = (args) => {
  const style = {
    cursor: "pointer",
  };

  if (args.node.group === true) {
    style.background = "#ced5e4";
    style.fontWeight = "700";
  }
  return style;
};
const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  suppressMenu: true,
  enableValue: true,
  enableRowGroup: true,
  //enablePivot: true,
 // floatingFilter: true,
  tooltipComponent: 'customTooltip',
 // filterParams: { newRowsAction: "keep" },
};

const autoGroupColumnDef = {
  headerName: "Group",
  X_formatType: "TEXT",
  suppressMenu: true,
  //enablePivot: true,
  enableRowGroup: false,
  pinned: "left",
  cellRendererParams: {
    suppressCount: true,
    innerRenderer: "GroupCellRenderer",
  },
  width: 350,
  rowDrag: false,
  filter: "agTextColumnFilter",
  suppressSizeToFit: true,
  suppressMovable: true,
  cellStyle: {
    backgroundColor: "unset",
    paddingRight: 0,
  },

  // X_backgroundColor: "#FFFF00",
  valueGetter: (params) => {
    return params.key;
  },
};

const sideBar = {
  toolPanels: [
    {
      id: "pivot",
      labelDefault: "Pivot",
      labelKey: "columns",
      iconKey: "pivotPanel",
      toolPanel: "agColumnsToolPanel",
    },
  ],
};

const gridFrameworkComponents = {
  GroupCellRenderer,
  // ...frameworkComponents,
  customTooltip: CustomTooltip 
};

const Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0; /* allow children to shrink */
`;


const TotalsBar = styled.div`
  flex: 0 0 auto;
  padding: 4px 8px;
  background: #f5f7fa;
  border-top: 1px solid #d0d5dd;
  font-size: 12px;
  line-height: 1.4;
  display: flex;
  align-items: center;
`;

const ResultGrid = () => {
  const rowData = useRowData();
  const columnDefs =useColumnDefinitionsFromCommitedState()
  const [grid, setGrid] = useGridInstance();
  const [displayedRowCount, setDisplayedRowCount] = useState(0); // rows currently shown after filters/grouping
  const hideGridSideBar = useSelector(selectHideGridSideBar);
  const onGridReady = useCallback(
    (grid) => {
      setGrid(grid);
    },
    [setGrid]
  );

  const getContextMenuItems = useCallback(() => {
    return ["copy", "copyWithHeaders"];
  }, []);

  const onColumnResized = (params) => {
    console.log("Column Resized...");
    params.api.resetRowHeights();
  };

  const onDataChange = (params) => {
    console.log("Data Changed...");
    addAggregateRowsForExcelExport(params);
  updateDisplayedRowCount();
  };

  const onFilterChange = (params) => {
    console.log("Filter Changed...");
    addAggregateRowsForExcelExport(params);
  updateDisplayedRowCount();
  };

  const onColumnRowGroupAndAggChange = (params) => {
    console.log("Row Group Changed...");
   /*
    The purpose of it is to save stateful column states (Row Group & AggFunc in our case). 
    Since the state of the grid is handled by @ls/grid-state-module, we cannot set the stateful attributes from our app
    But, we can dispatch an event, that @ls/grid-state-module is listening for, that would update the
    Currently, @ls/grid-state-module will update the grid state for these events: (columnMoved, columnPinned, columnResized, columnVisible)
    */
    if (rowData !=null) {
      var event = {
        type: "columnMoved",
      };
      console.log("Dispatching event....");
      grid.api.dispatchEvent(event);
    }
    
    addAggregateRowsForExcelExport(params);
  updateDisplayedRowCount();
  };
 /*
  Have to do this for row groups because the custom export for the @ls/react-grid does not support it
  by default atm. So adding the aggregrated values on the group node
  This method is called when row groups,agg columns or filter values change
  */
  const addAggregateRowsForExcelExport = () => {
    console.log("Adding aggregate rows for excel export...");

    if (grid) {
      grid.api.forEachNodeAfterFilterAndSort(function (rowNode, index) {
        if (rowNode.group) {
          const merged = { ...rowNode.aggData, ...rowNode.groupData };
        // const merged = { ...rowNode.aggData};
         console.log(rowNode)
          rowNode.setData(merged);      
        }
      });
    }
  };

  // Recalculate the number of displayed rows (post filter/sort/group)
  const updateDisplayedRowCount = useCallback(() => {
    if (!grid) return;
    let leafCount = 0;
    grid.api.forEachNodeAfterFilterAndSort(node => {
      if (!node.group) leafCount++;
    });
    setDisplayedRowCount(leafCount);
  }, [grid]);

  // Initialize / refresh count when grid instance or underlying data changes
  useEffect(() => {
    updateDisplayedRowCount();
  }, [grid, rowData, updateDisplayedRowCount]);

  useEffect(() => {

    if (grid) {
      grid.api.gridOptionsWrapper.setProperty(
        "getContextMenuItems",
        getContextMenuItems
      );
    }
  }, [grid, getContextMenuItems]);

  

  if (process.env.REACT_APP_IN2_ENV !== "PRD") {
    console.log("rowData", rowData);
    console.log("columnDefs", columnDefs);
  }

  const bandedRows = useSelector(selectBandedRows);
  const enableVerticalGridLines = useSelector(selectGridVerticalLines);
  const fontSize = useSelector(selectGridFontSize);
  const rowHeight = useSelector(selectGridRowHeight);
  const gridStateModuleAzure = useGridStateModuleAzure();
  const gridStateModuleLegacy = useGridStateModuleLegacy();

  const gridStateModule =
    process.env.REACT_APP_HOST_ENV === "AZURE"
      ? gridStateModuleAzure
      : gridStateModuleLegacy;
  return (
    <Root>
      {/* <GridWrapper> */}
      <Grid
        getRowStyle={getRowStyle}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        // groupRowRendererParams={groupRowRendererParams}
        frameworkComponents={gridFrameworkComponents}
        gridStateModule={gridStateModule}
        sideBar={sideBar}
        bandedRows={bandedRows}
        enableVerticalGridLines={enableVerticalGridLines}
        fontSize={fontSize}
        rowHeight={rowHeight}
        onGridReady={onGridReady}
        rowData={rowData}
        columnDefs={columnDefs}
        suppressContextMenu={false}
        headerHeight={50}
        groupDefaultExpanded={1}
        rowSelection="multiple"
        suppressCellSelection
        floatingFilter
        enableRangeSelection
        enableActionButtons
        onColumnResized={onColumnResized}
        onColumnVisible={onColumnResized} //Column size needs to be  changed when hide/unhide
        onColumnRowGroupChanged={onColumnRowGroupAndAggChange}
        onColumnValueChanged={onColumnRowGroupAndAggChange}
        onRowDataChanged={onDataChange}
        onFilterChanged={onFilterChange}
       // onColumnPivotModeChanged={onColumnPivotModeChange}
        tooltipShowDelay={0}
        enableBrowserTooltips={true}
        X_hideSideBar={hideGridSideBar}
        onModelUpdated={updateDisplayedRowCount}
        X_exportURL={process.env.REACT_APP_EXPORT_SERVICE || null}
      />
     {/* </GridWrapper> */}
      <TotalsBar>
        <span style={{fontWeight:600}}>Total Trades: {rowData?.length ?? 0}</span>
        <span style={{marginLeft:12}}>Displayed: {displayedRowCount}</span>
 
      </TotalsBar>

    </Root>
  );
};

export default memo(ResultGrid);
